import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { Observable } from "rxjs";
import {
  IAppointment,
  IAssignTeamMemberRequest,
  IAssignTeamMemberResponse,
  IBusinessAppointment, ICalendarAvailabilityBlock,
  ICustomerAppointment
} from "../interfaces";

@Injectable()
export class AppointmentService {
  constructor(private http: HttpClient) {

  }

  public createAppointment(data): Observable<any> {
    return this.http.post(`${environment.apiUrl}api/Appointment/Create`, data);
  }

  public createAppointmentFromCalendar(data): Observable<any> {
    return this.http.post(`${environment.apiUrl}api/Appointment/CreateFromCalendar`, data);
  }

  public createAvailabilityBlock(data): Observable<ICalendarAvailabilityBlock> {
    return this.http.post<ICalendarAvailabilityBlock>(`${environment.apiUrl}api/Calendar/CreateAvailabilityBlock`, data);
  }

  public updateAvailabilityBlock(data): Observable<ICalendarAvailabilityBlock> {
    return this.http.put<ICalendarAvailabilityBlock>(`${environment.apiUrl}api/Calendar/UpdateAvailabilityBlock`, data);
  }

  public getAvailabilityBlocks(getData: any, teamMemberId?: number): Observable<ICalendarAvailabilityBlock[]> {
    const params: any = {
      end: getData?.end.toJSON(),
      start: getData?.start.toJSON()
    };

    if (Intl?.DateTimeFormat) {
      params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    if (teamMemberId) {
      params.teamMemberId = teamMemberId;
    }

    return this.http.get<ICalendarAvailabilityBlock[]>(`${environment.apiUrl}api/Calendar/AvailabilityBlocks`, {
      params: params
    });
  }

  public deleteAvailabilityBlock(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}api/Calendar/DeleteAvailabilityBlock`, { params: { id } });
  }

  public getBusinessAppointments(getData: any, teamMemberId?: number): Observable<IBusinessAppointment[]> {
    const params: any = {};

    if (getData?.start) {
      params.start = getData.start.toJSON();
    }

    if (getData?.end) {
      params.end = getData.end.toJSON();
    }

    if (Intl?.DateTimeFormat) {
      params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    // Add teamMemberId only if it has a value
    if (teamMemberId) {
      params.teamMemberId = teamMemberId;
    }

    // Without statuses the API returns only active (pending/confirmed) bookings.
    if (getData?.statuses?.length) {
      params.statuses = getData.statuses;
    }
    return this.http.get<IBusinessAppointment[]>(`${environment.apiUrl}api/Appointment/GetBusinessAppointments`, {
      params: params
    });
  }

  public getForCalendar(getData: any, teamMemberId: number): Observable<IBusinessAppointment[]> {
    const params: any = {
      end: getData?.end.toJSON(),
      start: getData?.start.toJSON()
    };

    if (Intl?.DateTimeFormat) {
      params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    // Add teamMemberId only if it has a value
    if (teamMemberId) {
      params.teamMemberId = teamMemberId;
    }

    return this.http.get<IAppointment[]>(`${environment.apiUrl}api/Appointment/CalendarAppointments`, {
      params: params
    });
  }

  public getCustomerAppointments(): Observable<ICustomerAppointment[]> {
    return this.http.get<ICustomerAppointment[]>(`${environment.apiUrl}api/Appointment/GetCustomerAppointments`);
  }

  public cancelAppointments(params): Observable<IAppointment[]> {
    return this.http.post<IAppointment[]>(`${environment.apiUrl}api/Appointment/Cancel`, params);
  }

  public deleteAppointment(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}api/Appointment/Delete`, { params: { appointmentId: id } });
  }

  public confirmAppointments(params): Observable<IAppointment[]> {
    return this.http.post<IAppointment[]>(`${environment.apiUrl}api/Appointment/Confirm`, params);
  }
  public declineAppointments(params): Observable<IAppointment[]> {
    return this.http.post<IAppointment[]>(`${environment.apiUrl}api/Appointment/Decline`,params);
  }

  public assignTeamMember(data: IAssignTeamMemberRequest): Observable<IAssignTeamMemberResponse> {
    return this.http.post<IAssignTeamMemberResponse>(`${environment.apiUrl}api/Appointment/AssignTeamMember`, data);
  }

  public updateAppointment(data): Observable<IAppointment> {
    return this.http.put<IAppointment>(`${environment.apiUrl}api/Appointment/Update`, data);
  }

  // Public "manage booking" page: the token from the booking email identifies the booking.
  public getByToken(publicToken: string): Observable<IAppointment> {
    return this.http.get<IAppointment>(`${environment.apiUrl}api/Appointment/GetByToken`, { params: { publicToken } });
  }

  public cancelByToken(publicToken: string, cancellationReason: string | null): Observable<IAppointment> {
    return this.http.post<IAppointment>(`${environment.apiUrl}api/Appointment/CancelByToken`, { publicToken, cancellationReason });
  }
}
